import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import ViewToggle from './components/ViewToggle';
import EventFilters from '../../components/events/EventFilters';
import EventList from './components/EventList';
import MapView from './components/MapView';
import useEvents from '../../hooks/useEvents';

const EventDiscovery = () => {
  const navigate = useNavigate();
  const [activeView, setActiveView] = useState('list');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    city: 'all',
    difficulty: 'all',
    eventType: 'all',
    dateFrom: '',
    dateTo: '',
    maxPrice: 50000
  });

  // Use the new useEvents hook with filters
  const { events, loading, error } = useEvents(filters);

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      city: 'all',
      difficulty: 'all',
      eventType: 'all',
      dateFrom: '',
      dateTo: '',
      maxPrice: 50000
    });
  };

  const handleEventClick = (event) => {
    if (!event?.id) {
      console.error('Event ID is missing');
      return;
    }
    navigate(`/event-details/${event?.id}`);
  };

  // Error State
  if (error) {
    return (
      <>
        <Header />
        <div className="main-content">
          <div className="min-h-screen bg-background">
            <div className="bg-gradient-to-br from-primary to-secondary text-primary-foreground py-8 md:py-12 lg:py-16">
              <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-2">Etkinlikleri Keşfet</h1>
                <p className="text-sm md:text-base lg:text-lg opacity-90">
                  Türkiye genelinde SUP etkinliklerini keşfedin
                </p>
              </div>
            </div>
            
            <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-12">
              <div className="text-center max-w-md mx-auto">
                <div className="bg-red-100 p-4 rounded-full inline-flex mb-4">
                  <Icon name="AlertTriangle" size={48} className="text-red-600" />
                </div>
                <h2 className="text-2xl font-bold text-foreground mb-2">Etkinlikler Yüklenemedi</h2>
                <p className="text-muted-foreground mb-6">
                  {error}
                </p>
                <button
                  onClick={() => window?.location?.reload()}
                  className="bg-primary hover:bg-primary/90 text-primary-foreground font-medium py-2 px-6 rounded-lg flex items-center justify-center gap-2 mx-auto transition-colors"
                >
                  <Icon name="RefreshCw" size={18} />
                  Tekrar Dene
                </button>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="main-content">
        <div className="min-h-screen bg-background">
          {/* Hero Section */}
          <div className="bg-gradient-to-br from-primary to-secondary text-primary-foreground py-8 md:py-12 lg:py-16">
            <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 md:gap-6">
                <div>
                  <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-2">
                    Etkinlikleri Keşfet
                  </h1>
                  <p className="text-sm md:text-base lg:text-lg opacity-90">
                    Türkiye genelinde SUP etkinliklerini keşfedin ve hemen katılın
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-12">
            <div className="flex flex-col lg:flex-row gap-6 md:gap-8">
              {/* Filters Sidebar */}
              <aside className="lg:w-80 flex-shrink-0">
                <EventFilters
                  filters={filters}
                  onFilterChange={handleFilterChange}
                  onClearFilters={handleClearFilters}
                  isOpen={isFilterOpen}
                  onToggle={() => setIsFilterOpen(!isFilterOpen)}
                />
              </aside>

              {/* Events Content */}
              <main className="flex-1 min-w-0">
                {/* View Toggle & Results Count */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 md:mb-8">
                  <div className="flex items-center gap-3">
                    <Icon name="Calendar" size={20} className="text-primary" />
                    <span className="text-sm md:text-base text-muted-foreground">
                      {events?.length} etkinlik bulundu
                    </span>
                  </div>
                  <ViewToggle activeView={activeView} onViewChange={setActiveView} />
                </div>

                {/* Content Area */}
                {loading ? (
                  <div className="flex items-center justify-center min-h-[400px]">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                      <p className="text-muted-foreground">Etkinlikler yükleniyor...</p>
                    </div>
                  </div>
                ) : events?.length === 0 ? (
                  <div className="text-center py-12">
                    <Icon name="Calendar" size={48} className="text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-foreground mb-2">
                      Etkinlik Bulunamadı
                    </h3>
                    <p className="text-muted-foreground mb-6">
                      Arama kriterlerinize uygun etkinlik bulunamadı. Filtreleri değiştirmeyi deneyin.
                    </p>
                    <button
                      onClick={handleClearFilters}
                      className="bg-primary hover:bg-primary/90 text-primary-foreground font-medium py-2 px-6 rounded-lg transition-colors"
                    >
                      Filtreleri Temizle
                    </button>
                  </div>
                ) : activeView === 'list' ? (
                  <EventList events={events} isLoading={false} />
                ) : (
                  <MapView events={events} onEventClick={handleEventClick} />
                )}
              </main>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default EventDiscovery;